import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { apiService } from '@/services/api.service';
import { loadPackTextureUrl } from '@/services/packAssets';
import styles from './BedrockModelViewer.module.scss';

export const S = 1 / 16; // 16 px Bedrock = 1 bloc
// Ordre des matériaux d'une BoxGeometry three.js : +X, -X, +Y, -Y, +Z, -Z
const FACE_ORDER = ['east', 'west', 'up', 'down', 'south', 'north'] as const;

export interface BoxSpec {
  mode: string; // 'full' | 'none' | 'custom'
  origin?: number[] | null;
  size?: number[] | null;
}

interface BedrockModelViewerProps {
  groupId: string;
  /** Chemin (relatif au groupe) du fichier .geo.json — mode géométrie si présent. */
  geometryFile?: string | null;
  /** Identifiant de géométrie à sélectionner dans le tableau minecraft:geometry. */
  geometryId?: string;
  /** Texture principale (atlas) pour les UV de la géométrie, ou texture du cube simple. */
  texturePath?: string | null;
  /** Textures par face (fallback cube simple sans géométrie). */
  faceTextures?: Record<string, string>;
  renderMethod?: string;
  /** Aperçu d'un fichier en attente (non enregistré) : objectURL de texture / texte de géométrie. */
  overrideTextureUrl?: string | null;
  overrideGeometryText?: string | null;
  /** Affiche une grille de sol et positionne le modèle en coordonnées bloc (pour les blocks). */
  showGround?: boolean;
  /** Entité debout : coordonnées réelles (pieds à y=0) + cadrage auto, pour aligner la box de collision. */
  standing?: boolean;
  /** Box de collision à visualiser (rouge). */
  collisionBox?: BoxSpec | null;
  /** Box de sélection à visualiser (cyan). */
  selectionBox?: BoxSpec | null;
  className?: string;
}

/** Charge une image de pack via un blob authentifié mis en cache (les <img>/loaders three ne portent pas le JWT). */
export async function loadPackImage(groupId: string, path: string): Promise<HTMLImageElement | null> {
  const url = await loadPackTextureUrl(groupId, path); // URL partagée/cachée : ne pas révoquer
  if (!url) return null;
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.crossOrigin = 'anonymous';
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
  } catch {
    return null;
  }
}

/** Charge une image depuis une URL directe (ex. objectURL d'un fichier en attente). */
export function loadImageDirect(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => resolve(i);
    i.onerror = () => resolve(null);
    i.src = url;
  });
}

function applyRenderMethod(opts: THREE.MeshPhongMaterialParameters, renderMethod?: string) {
  if (renderMethod === 'alpha_test') {
    opts.alphaTest = 0.5;
    opts.transparent = true;
  } else if (renderMethod === 'blend') {
    opts.transparent = true;
  }
}

/** Texture pixel-art depuis une image (crop de la 1ère frame si image verticale multi-frame). */
function makeTexture(img: HTMLImageElement): THREE.Texture {
  let tex: THREE.Texture;
  if (img.naturalHeight > img.naturalWidth && img.naturalWidth > 0) {
    const c = document.createElement('canvas');
    c.width = img.naturalWidth;
    c.height = img.naturalWidth;
    c.getContext('2d')!.drawImage(img, 0, 0, img.naturalWidth, img.naturalWidth, 0, 0, img.naturalWidth, img.naturalWidth);
    tex = new THREE.CanvasTexture(c);
  } else {
    tex = new THREE.Texture(img);
    tex.needsUpdate = true;
  }
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Wireframe d'une box Bedrock (collision/selection) en coordonnées bloc. */
function boxWireframe(spec: BoxSpec | null | undefined, color: number): THREE.LineSegments | null {
  if (!spec || spec.mode === 'none') return null;
  let src = { origin: [-8, 0, -8], size: [16, 16, 16] };
  if (spec.mode === 'custom' && spec.origin?.length === 3 && spec.size?.length === 3) {
    src = { origin: spec.origin, size: spec.size };
  }
  const [ox, oy, oz] = [src.origin[0] ?? 0, src.origin[1] ?? 0, src.origin[2] ?? 0];
  const [sx, sy, sz] = [src.size[0] ?? 0, src.size[1] ?? 0, src.size[2] ?? 0];
  const geo = new THREE.BoxGeometry(Math.max(0.001, sx * S), Math.max(0.001, sy * S), Math.max(0.001, sz * S));
  // depthTest désactivé : la hitbox reste visible même à travers un modèle plein (overlay).
  const line = new THREE.LineSegments(
    new THREE.EdgesGeometry(geo),
    new THREE.LineBasicMaterial({ color, depthTest: false, transparent: true }),
  );
  line.renderOrder = 999;
  line.position.set((ox + sx / 2) * S, (oy + sy / 2) * S, (oz + sz / 2) * S);
  geo.dispose();
  return line;
}

/** Construit le mesh d'une géométrie Bedrock (bones/cubes + UV par face slicées dans l'atlas). */
export function buildGeometryMesh(geoEntry: any, atlasImg: HTMLImageElement, renderMethod?: string, recenter = true): THREE.Group {
  const desc = geoEntry.description || {};
  const texW = desc.texture_width || 16;
  const texH = desc.texture_height || 16;
  const scX = atlasImg.naturalWidth / texW;
  const scY = atlasImg.naturalHeight / texH;

  const root = new THREE.Group();
  const boneGroups: Record<string, THREE.Group> = {};
  const bones = geoEntry.bones || [];
  for (const bone of bones) {
    const g = new THREE.Group();
    g.name = bone.name;
    boneGroups[bone.name] = g;
  }

  const pivotOf = (name: string): [number, number, number] => {
    const b = bones.find((x: any) => x.name === name);
    return b && Array.isArray(b.pivot) ? b.pivot : [0, 0, 0];
  };
  for (const bone of bones) {
    const group = boneGroups[bone.name]!;
    const parentBone = bone.parent && boneGroups[bone.parent] ? bone.parent : null;
    const parent = parentBone ? boneGroups[parentBone]! : root;
    parent.add(group);

    // Position de l'os (pivots en espace modèle, relatif au parent) + rotation d'os.
    const [bpx, bpy, bpz] = (Array.isArray(bone.pivot) ? bone.pivot : [0, 0, 0]) as [number, number, number];
    const [ppx, ppy, ppz] = parentBone ? pivotOf(parentBone) : [0, 0, 0];
    group.position.set((bpx - ppx) * S, (bpy - ppy) * S, (bpz - ppz) * S);
    if (Array.isArray(bone.rotation)) {
      group.rotation.set(
        (bone.rotation[0] * Math.PI) / 180,
        (bone.rotation[1] * Math.PI) / 180,
        (bone.rotation[2] * Math.PI) / 180,
      );
    }

    for (const cube of bone.cubes || []) {
      const [ox, oy, oz] = cube.origin;
      const [cw, ch, cd] = cube.size;
      const inflate = cube.inflate || 0;
      const hasZero = cw === 0 || ch === 0 || cd === 0;
      // UV "box" (uv = [U, V]) des géométries vanilla/anciennes, ou UV par face (1.12+).
      const boxUv: [number, number] | null = Array.isArray(cube.uv) ? cube.uv : null;
      const uvData = !boxUv && cube.uv ? cube.uv : {};
      // Dépliage box UV (perso vers +Z : face avant = south).
      const faceRegion = (face: string): [number, number, number, number] | null => {
        if (boxUv) {
          const [U, V] = boxUv;
          switch (face) {
            case 'west': return [U, V + cd, cd, ch];
            case 'south': return [U + cd, V + cd, cw, ch];
            case 'east': return [U + cd + cw, V + cd, cd, ch];
            case 'north': return [U + 2 * cd + cw, V + cd, cw, ch];
            case 'up': return [U + cd, V, cw, cd];
            case 'down': return [U + cd + cw, V, cw, cd];
            default: return null;
          }
        }
        const f = uvData[face];
        if (!f || !f.uv_size) return null;
        return [f.uv[0], f.uv[1], f.uv_size[0], f.uv_size[1]];
      };

      const materials = FACE_ORDER.map((face) => {
        const reg = faceRegion(face);
        if (!reg) {
          return new THREE.MeshPhongMaterial({ color: 0x888888, side: hasZero ? THREE.DoubleSide : THREE.FrontSide });
        }
        const [u, v, du, dv] = reg;
        const pw = Math.max(1, Math.round(Math.abs(du) * scX));
        const ph = Math.max(1, Math.round(Math.abs(dv) * scY));
        const sx = Math.round(Math.min(u, u + du) * scX);
        const sy = Math.round(Math.min(v, v + dv) * scY);

        const c = document.createElement('canvas');
        c.width = pw;
        c.height = ph;
        const ctx = c.getContext('2d')!;
        ctx.save();
        if (du < 0) { ctx.translate(pw, 0); ctx.scale(-1, 1); }
        if (dv < 0) { ctx.translate(0, ph); ctx.scale(1, -1); }
        ctx.drawImage(atlasImg, sx, sy, pw, ph, 0, 0, pw, ph);
        ctx.restore();

        const tex = new THREE.CanvasTexture(c);
        tex.magFilter = THREE.NearestFilter;
        tex.minFilter = THREE.NearestFilter;
        tex.colorSpace = THREE.SRGBColorSpace;
        const opts: THREE.MeshPhongMaterialParameters = { map: tex };
        if (hasZero) opts.side = THREE.DoubleSide;
        applyRenderMethod(opts, renderMethod);
        return new THREE.MeshPhongMaterial(opts);
      });

      // inflate agrandit la boîte de `inflate` sur chaque face (couches d'armure notamment).
      const gw = cw + 2 * inflate;
      const gh = ch + 2 * inflate;
      const gd = cd + 2 * inflate;
      const gox = ox - inflate;
      const goy = oy - inflate;
      const goz = oz - inflate;
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(gw * S || 0.001, gh * S || 0.001, gd * S || 0.001), materials);
      // Centre du cube, exprimé relativement au pivot de l'os (le groupe d'os est placé au pivot).
      const cx = gox + gw / 2;
      const cy = goy + gh / 2;
      const cz = goz + gd / 2;

      if (cube.pivot && cube.rotation) {
        const pivot = new THREE.Group();
        const [px, py, pz] = cube.pivot;
        pivot.position.set((px - bpx) * S, (py - bpy) * S, (pz - bpz) * S);
        mesh.position.set((cx - px) * S, (cy - py) * S, (cz - pz) * S);
        pivot.rotation.set(
          (cube.rotation[0] * Math.PI) / 180,
          (cube.rotation[1] * Math.PI) / 180,
          (cube.rotation[2] * Math.PI) / 180,
        );
        pivot.add(mesh);
        group.add(pivot);
      } else {
        mesh.position.set((cx - bpx) * S, (cy - bpy) * S, (cz - bpz) * S);
        group.add(mesh);
      }
    }
  }

  // En mode sol (blocks) : on garde les coordonnées bloc pour aligner sol + box.
  // Sinon (entities) : on recentre et on met à l'échelle pour tenir dans la vue.
  if (recenter) {
    const box = new THREE.Box3().setFromObject(root);
    const center = new THREE.Vector3();
    box.getCenter(center);
    root.position.sub(center);
    const size = new THREE.Vector3();
    box.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);
    if (maxDim > 1.5) root.scale.setScalar(1.2 / maxDim);
  }
  return root;
}

/** Cube 1×1×1 simple (block sans géométrie), textures par face ou texture unique. */
function buildCubeMesh(faceImgs: Partial<Record<string, HTMLImageElement>> | null, single: HTMLImageElement | null, renderMethod?: string): THREE.Mesh {
  const geo = new THREE.BoxGeometry(1, 1, 1);
  const mat = (img: HTMLImageElement | undefined | null): THREE.Material => {
    if (!img) return new THREE.MeshPhongMaterial({ color: 0x888888 });
    const opts: THREE.MeshPhongMaterialParameters = { map: makeTexture(img) };
    applyRenderMethod(opts, renderMethod);
    return new THREE.MeshPhongMaterial(opts);
  };
  if (faceImgs && Object.keys(faceImgs).length > 0) {
    const materials = FACE_ORDER.map((face) => mat(faceImgs[face] || faceImgs['*'] || single));
    return new THREE.Mesh(geo, materials);
  }
  return new THREE.Mesh(geo, mat(single));
}

export function BedrockModelViewer({
  groupId,
  geometryFile,
  geometryId,
  texturePath,
  faceTextures,
  renderMethod,
  overrideTextureUrl,
  overrideGeometryText,
  showGround,
  collisionBox,
  selectionBox,
  standing,
  className,
}: BedrockModelViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const meshRef = useRef<THREE.Object3D | null>(null);
  const boxesRef = useRef<THREE.Group | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const targetRef = useRef(new THREE.Vector3(0, showGround ? 0.45 : standing ? 1 : 0, 0));
  // Orbite caméra (coord. sphériques autour de la cible).
  const camRef = useRef({ theta: Math.PI / 4, phi: showGround ? 1.15 : 1.25, radius: standing ? 3.4 : 2.7, dragging: false });
  const [status, setStatus] = useState<'loading' | 'ready' | 'empty' | 'error'>('loading');

  // --- Cycle de vie de la scène (monté une fois) ---
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const w = container.clientWidth || 320;
    const h = container.clientHeight || 320;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(w, h, false);

    const scene = new THREE.Scene();
    sceneRef.current = scene;
    const camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 1000);
    scene.add(new THREE.AmbientLight(0xffffff, 0.7));
    const dir = new THREE.DirectionalLight(0xffffff, 0.9);
    dir.position.set(5, 8, 5);
    scene.add(dir);

    // Sol + case du joueur + groupe des box (pour les blocks).
    if (showGround) {
      const grid = new THREE.GridHelper(3, 3, 0x8b3ff1, 0x3a3a4a);
      scene.add(grid);

      // Case où se tient le joueur qui pose le bloc (référence d'orientation, +Z).
      const tile = new THREE.Mesh(
        new THREE.PlaneGeometry(1, 1),
        new THREE.MeshBasicMaterial({ color: 0x22c55e, transparent: true, opacity: 0.3, side: THREE.DoubleSide }),
      );
      tile.rotation.x = -Math.PI / 2;
      tile.position.set(0, 0.015, 1);
      scene.add(tile);
      const pawn = new THREE.Mesh(
        new THREE.ConeGeometry(0.16, 0.5, 4),
        new THREE.MeshBasicMaterial({ color: 0x22c55e }),
      );
      pawn.position.set(0, 0.28, 1);
      pawn.rotation.y = Math.PI / 4;
      scene.add(pawn);
    }

    // Groupe des box (collision/sélection) : disponible dans tous les modes (block ou entité).
    const boxes = new THREE.Group();
    boxesRef.current = boxes;
    scene.add(boxes);

    const applyCamera = () => {
      const c = camRef.current;
      const t = targetRef.current;
      camera.position.set(
        t.x + c.radius * Math.sin(c.phi) * Math.sin(c.theta),
        t.y + c.radius * Math.cos(c.phi),
        t.z + c.radius * Math.sin(c.phi) * Math.cos(c.theta),
      );
      camera.lookAt(t);
    };

    let raf = 0;
    let lastInteract = performance.now();
    const animate = () => {
      raf = requestAnimationFrame(animate);
      const c = camRef.current;
      // Auto-orbite douce pour les entities ; en mode sol le point de vue reste fixe.
      if (!showGround && !c.dragging && performance.now() - lastInteract > 2000) c.theta += 0.003;
      applyCamera();
      renderer.render(scene, camera);
    };
    animate();

    // Contrôles : on déplace la caméra (orbite), pas le modèle.
    let px = 0;
    let py = 0;
    const onDown = (e: PointerEvent) => {
      camRef.current.dragging = true;
      px = e.clientX;
      py = e.clientY;
      lastInteract = performance.now();
      canvas.setPointerCapture(e.pointerId);
    };
    const onMove = (e: PointerEvent) => {
      if (!camRef.current.dragging) return;
      const dx = e.clientX - px;
      const dy = e.clientY - py;
      px = e.clientX;
      py = e.clientY;
      const c = camRef.current;
      c.theta -= dx * 0.01;
      // phi : petit = vue du dessus, grand = vue du dessous (glisser vers le bas pour voir dessous).
      c.phi = Math.max(0.1, Math.min(Math.PI - 0.1, c.phi + dy * 0.01));
      lastInteract = performance.now();
    };
    const onUp = () => {
      camRef.current.dragging = false;
      lastInteract = performance.now();
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const c = camRef.current;
      c.radius = Math.max(1.2, Math.min(6, c.radius * (e.deltaY > 0 ? 1.1 : 0.9)));
      lastInteract = performance.now();
    };
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    canvas.addEventListener('wheel', onWheel, { passive: false });

    const onResize = () => {
      const nw = container.clientWidth || 320;
      const nh = container.clientHeight || 320;
      renderer.setSize(nw, nh, false);
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
    };
    const ro = new ResizeObserver(onResize);
    ro.observe(container);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('wheel', onWheel);
      disposeObject(meshRef.current);
      meshRef.current = null;
      disposeObject(boxesRef.current);
      boxesRef.current = null;
      renderer.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- (Re)construction du mesh quand les entrées changent ---
  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    (async () => {
      const scene = sceneRef.current;
      if (!scene) return;

      // Texture principale : fichier en attente prioritaire, sinon celle du pack.
      const loadPrimary = async (): Promise<HTMLImageElement | null> => {
        if (overrideTextureUrl) return loadImageDirect(overrideTextureUrl);
        if (texturePath) return loadPackImage(groupId, texturePath);
        return null;
      };

      let mesh: THREE.Object3D | null = null;
      try {
        // Géométrie : fichier en attente prioritaire, sinon celle du pack.
        let geoEntry: any = null;
        let geoJson: any = null;
        if (overrideGeometryText) {
          try { geoJson = JSON.parse(overrideGeometryText); } catch { geoJson = null; }
        } else if (geometryFile && geometryId) {
          geoJson = (await apiService.getPackFileJson(groupId, geometryFile)).data;
        }
        const entries = geoJson?.['minecraft:geometry'];
        if (Array.isArray(entries)) {
          geoEntry = entries.find((g: any) => g?.description?.identifier === geometryId) || entries[0];
        }

        if (geoEntry) {
          const atlas = await loadPrimary();
          if (atlas && !cancelled) mesh = buildGeometryMesh(geoEntry, atlas, renderMethod, !showGround && !standing);
        }

        if (!mesh && !cancelled) {
          // Fallback cube simple.
          const faceImgs: Record<string, HTMLImageElement> = {};
          if (faceTextures && !overrideTextureUrl) {
            const uniquePaths = Array.from(new Set(Object.values(faceTextures)));
            const loaded = new Map<string, HTMLImageElement>();
            await Promise.all(
              uniquePaths.map(async (p) => {
                const img = await loadPackImage(groupId, p);
                if (img) loaded.set(p, img);
              }),
            );
            for (const [face, p] of Object.entries(faceTextures)) {
              const img = loaded.get(p);
              if (img) faceImgs[face] = img;
            }
          }
          const single = await loadPrimary();
          if (!cancelled && (Object.keys(faceImgs).length > 0 || single)) {
            mesh = buildCubeMesh(Object.keys(faceImgs).length ? faceImgs : null, single, renderMethod);
            if (showGround) mesh.position.y = 0.5; // pose le cube sur le sol (0..1)
          }
        }
      } catch {
        if (!cancelled) setStatus('error');
      }

      if (cancelled) {
        disposeObject(mesh);
        return;
      }

      // Remplace le mesh courant.
      if (meshRef.current) {
        scene.remove(meshRef.current);
        disposeObject(meshRef.current);
        meshRef.current = null;
      }
      if (mesh) {
        scene.add(mesh);
        meshRef.current = mesh;
        // Entité debout : cadre la caméra sur les bornes réelles du modèle (pour voir la box de collision).
        if (standing) {
          const bb = new THREE.Box3().setFromObject(mesh);
          if (isFinite(bb.min.x)) {
            const center = bb.getCenter(new THREE.Vector3());
            const size = bb.getSize(new THREE.Vector3());
            targetRef.current.set(center.x, center.y, center.z);
            camRef.current.radius = Math.max(size.x, size.y, size.z, 0.5) * 2.2;
          }
        }
        setStatus('ready');
      } else {
        setStatus('empty');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [groupId, geometryFile, geometryId, texturePath, faceTextures, renderMethod, showGround, standing, overrideTextureUrl, overrideGeometryText]);

  // --- (Re)dessine les box de collision/sélection en live ---
  const boxesKey = JSON.stringify({ c: collisionBox, s: selectionBox });
  useEffect(() => {
    const boxes = boxesRef.current;
    if (!boxes) return;
    while (boxes.children.length) {
      const child = boxes.children[0]!;
      boxes.remove(child);
      disposeObject(child);
    }
    const col = boxWireframe(collisionBox, 0xff5555);
    if (col) boxes.add(col);
    const sel = boxWireframe(selectionBox, 0x38bdf8);
    if (sel) boxes.add(sel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxesKey]);

  return (
    <div ref={containerRef} className={`${styles.viewer} ${className || ''}`}>
      <canvas ref={canvasRef} className={styles.canvas} />
      {status === 'loading' && <div className={styles.overlay}>Chargement du modèle…</div>}
      {status === 'empty' && <div className={styles.overlay}>Aucun aperçu 3D disponible</div>}
      {status === 'error' && <div className={styles.overlay}>Erreur de rendu</div>}
    </div>
  );
}

function disposeObject(obj: THREE.Object3D | null) {
  if (!obj) return;
  obj.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const mat = mesh.material;
    if (Array.isArray(mat)) mat.forEach(disposeMaterial);
    else if (mat) disposeMaterial(mat);
  });
}

function disposeMaterial(mat: THREE.Material) {
  const m = mat as THREE.MeshPhongMaterial;
  if (m.map) m.map.dispose();
  mat.dispose();
}
